import React, { ReactNode, useCallback } from "react";

import { Dialog, makeStyles, Typography } from "@material-ui/core";

import { useQueryString } from "modules/core/hooks/useQueryString";
import { useBoolean } from "modules/core/hooks/useBoolean";

import ImgPlaceholder from "modules/core/assets/img/efestra-logo-small.png";

const useStyles = makeStyles(() => ({
  image: {
    display: "block",
    maxWidth: "90vw",
    maxHeight: "calc(90vh - 40px)",
    objectFit: "contain",
    objectPosition: "center"
  },

  name: {
    height: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },

  backdrop: {
    backgroundColor: "#000000ea"
  }
}));

const ImageViewer: React.FC = () => {
  const { urlQueries, removeFromQS } = useQueryString();

  const imageUrl = urlQueries.img;

  const [loadingError, { on: setLoadingError, off: removeLoadingError }] =
    useBoolean(false);

  const closeHandler = useCallback(() => {
    removeFromQS({ keys: ["img"] });
    removeLoadingError();
  }, [removeFromQS, removeLoadingError]);

  const classes = useStyles();

  const pathArray = imageUrl ? (imageUrl as string)?.split("/") : null;
  const name = pathArray
    ? pathArray[pathArray.length - 1].split("?")?.[0]
    : null;
  return (
    <Dialog
      open={Boolean(imageUrl)}
      onClose={closeHandler}
      maxWidth="xl"
      BackdropProps={{ className: classes.backdrop }}
    >
      <div>
        <img
          src={loadingError ? ImgPlaceholder : (imageUrl as string)}
          alt=""
          className={classes.image}
          onError={setLoadingError}
          style={{
            filter: loadingError ? "opacity(0.2)" : "none"
          }}
        />

        <div className={classes.name}>
          <Typography variant="body2">{name}</Typography>
        </div>
      </div>
    </Dialog>
  );
};

export const useImageViewer = () => {
  const { urlQueries, addToQS, removeFromQS } = useQueryString();

  const openImage = useCallback(
    (url: string) => {
      addToQS({
        queryObject: { img: url }
      });
    },
    [addToQS]
  );

  const closeImage = useCallback(() => {
    removeFromQS({ keys: ["img"] });
  }, [removeFromQS]);

  return {
    imageUrl: urlQueries.img,
    openImage,
    closeImage
  };
};

type ImageViewerTriggerProps = {
  children: (openImage: (url: string) => void) => ReactNode;
};

export const ImageViewerTrigger: React.FC<ImageViewerTriggerProps> = ({
  children
}) => {
  const { openImage } = useImageViewer();

  return <>{children(openImage)}</>;
};

export default ImageViewer;
